import React from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import useCache from '../../app/useCache';
import { CachedEntity } from '../../app/cacheSlice';
import { setOrganizationUserRoles } from '../../app/userSlice';
import { useCurrentUser } from '../../app/useCurrentUser';
import useStagedRoles from '../../app/useStagedRoles';
import { OrganizationMemberModel, OrganizationModel } from '../../typings';
import RoleSelector from '../common/RoleSelector';
import { pendingChangesLabel } from './UserRolesPanel';

const getOrganizationRoles = (userOrganizations: OrganizationMemberModel[], organization: OrganizationModel | null) => {
    if (!organization) {
        return [];
    }
    const orgMember = userOrganizations.find(o => o.organizationId === organization.id);
    return orgMember ? orgMember.roles : [];
};

export default function UserOrganizationRolesPanel() {
    const dispatch = useAppDispatch();
    const currentUser = useAppSelector(store => store.currentUser);
    const userState = useAppSelector(store => store.user);
    const roles = useCache(CachedEntity.Role);
    const [isAdmin, ,] = useCurrentUser();

    const organizationId = currentUser.data?.organization?.id ?? 0;
    const committedIds = getOrganizationRoles(userState?.organizations, currentUser.data?.organization ?? null).map(r => r.id);
    const { draftIds, setDraftIds, added, removed, isDirty, reset } = useStagedRoles(committedIds);

    const disabled = !isAdmin || !userState?.data?.enabled;

    const handleSave = () => {
        dispatch(setOrganizationUserRoles({ userId: userState.id, organizationId, roleIds: draftIds }));
    };

    return (
        <React.Fragment>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
                        <Box>
                            <Typography variant="h5" color="primary">
                                Organization
                            </Typography>
                            {currentUser.data?.organization && (
                                <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                                    {currentUser.data.organization.displayName}
                                </Typography>
                            )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Typography variant="body2" color="textSecondary">
                                {pendingChangesLabel(added.length, removed.length)}
                            </Typography>
                            <Button variant="outlined" onClick={reset} disabled={disabled || !isDirty}>
                                Cancel
                            </Button>
                            {/*
                              No organization means organizationId falls back to 0, which the
                              backend validator rejects (OrganizationId GreaterThan 0). isAdmin
                              comes from JWT role claims alone, so an admin with no organization
                              is reachable - keep Save out of a state where it can only 400.
                              Cancel stays enabled: discarding a local draft is always safe.
                            */}
                            <Button variant="contained" onClick={handleSave} disabled={disabled || !isDirty || organizationId <= 0}>
                                Save
                            </Button>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <RoleSelector
                        roles={roles}
                        selectedIds={draftIds}
                        onChange={setDraftIds}
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
        </React.Fragment>
    );
}
